/**
 * Fund History Service
 * 
 * API functions for Fund Control Center
 * Handles data fetching from Firestore collections
 */

import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { DATABASE } from '../Config/firebase';

// Collection names
const FUND_HISTORY_COLLECTION = 'fund_history';
const FUND_SUMMARY_COLLECTION = 'fund_summary';

/**
 * Get fund summary (all-time statistics)
 */
export const getFundSummary = async () => {
  try {
    const docRef = doc(DATABASE, FUND_SUMMARY_COLLECTION, 'all_time');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        success: true,
        data: docSnap.data()
      };
    } else {
      return {
        success: false,
        error: 'Summary not found. Please run import script first.'
      };
    }
  } catch (error) {
    console.error('Error fetching fund summary:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get monthly history data
 * @param {Date} startDate - Optional start date filter
 * @param {Date} endDate - Optional end date filter
 */
export const getMonthlyHistory = async (startDate = null, endDate = null) => {
  try {
    let q = collection(DATABASE, FUND_HISTORY_COLLECTION);
    
    // Apply date filters if provided
    if (startDate && endDate) {
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth();
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth();
      
      q = query(
        q,
        where('year', '>=', startYear),
        where('year', '<=', endYear)
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    const months = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });
    
    // Additional filtering for month range if needed
    const filtered = months.filter(month => {
      if (!startDate || !endDate) return true;
      
      const monthDate = new Date(month.year, month.month);
      return monthDate >= startDate && monthDate <= endDate;
    });
    
    return {
      success: true,
      data: filtered
    };
  } catch (error) {
    console.error('Error fetching monthly history:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

const CORPORATE_CLIENTS_TO_IGNORE = [
  'Joao Renato Mascarin',
  'João Renato Mascarin',
  'Wilson Batizeli',
  'Ariana Arruda Pereira Batizeli',
  'Ariana Arruda'
];

function isCorporateUser(user) {
  if (!user) return false;
  const name = (user.full_name || user.name || '').toString().toLowerCase();
  return user.user_type === 1 || CORPORATE_CLIENTS_TO_IGNORE.some(c => name.includes(c.toLowerCase()));
}

function getLedgerAmount(ledger) {
  return parseFloat(ledger.new_txn_amount || ledger.amount || 0);
}

function getLedgerBalance(ledger) {
  return parseFloat(ledger.current_balance || ledger.balance || 0);
}

function getLedgerDate(ledger) {
  if (ledger.date?.toDate) return ledger.date.toDate();
  if (ledger.created_at?.toDate) return ledger.created_at.toDate();
  if (ledger.date) return new Date(ledger.date);
  return new Date();
}

/**
 * Get client analytics LIVE from Firestore (users + ledgers + transection_requests).
 * No script required – data updates when you load/refresh the page.
 * Includes all individual clients (including closed / balance 0).
 * N D = approved deposits count, N W = paid withdrawals count.
 */
export const getClientAnalyticsLive = async () => {
  try {
    const [usersSnap, ledgersSnap, transactionsSnap] = await Promise.all([
      getDocs(query(collection(DATABASE, 'users'), where('role', '==', 1))),
      getDocs(collection(DATABASE, 'ledgers')),
      getDocs(collection(DATABASE, 'transection_requests'))
    ]);

    const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const individualUsers = users.filter(u => !isCorporateUser(u));

    const ledgersByUser = {};
    ledgersSnap.docs.forEach(d => {
      const dta = d.data();
      const uid = dta.user_id;
      if (!ledgersByUser[uid]) ledgersByUser[uid] = [];
      ledgersByUser[uid].push({ id: d.id, ...dta });
    });
    Object.keys(ledgersByUser).forEach(uid => {
      ledgersByUser[uid].sort((a, b) => getLedgerDate(a) - getLedgerDate(b));
    });

    const transactionsByUser = {};
    transactionsSnap.docs.forEach(d => {
      const dta = d.data();
      const uid = dta.user_id;
      if (!transactionsByUser[uid]) transactionsByUser[uid] = [];
      transactionsByUser[uid].push(dta);
    });

    const clients = [];
    individualUsers.forEach(user => {
      const uid = user.id;
      const userLedgers = ledgersByUser[uid] || [];
      const userTxns = transactionsByUser[uid] || [];
      const approvedDeposits = userTxns.filter(t => t.request_type === 0 && t.request_status === 2);
      const paidWithdrawals = userTxns.filter(t => t.request_type === 1 && t.request_status === 5);

      let currentBalance = 0;
      let totalDeposits = 0;
      let totalWithdrawals = 0;
      let totalPerformance = 0;
      let firstLedgerDate = null;

      if (userLedgers.length > 0) {
        const deposits = userLedgers.filter(l => l.transection_type === 0);
        const withdrawals = userLedgers.filter(l => l.transection_type === 1 || l.transection_type === 2);
        const performance = userLedgers.filter(l => l.transection_type === 3);
        totalDeposits = deposits.reduce((s, l) => s + getLedgerAmount(l), 0);
        totalWithdrawals = withdrawals.reduce((s, l) => s + getLedgerAmount(l), 0);
        totalPerformance = performance.reduce((s, l) => s + getLedgerAmount(l), 0);
        const lastLedger = userLedgers[userLedgers.length - 1];
        currentBalance = getLedgerBalance(lastLedger);
        firstLedgerDate = getLedgerDate(userLedgers[0]);
      }

      clients.push({
        name: user.full_name || user.name || '—',
        clientNumber: uid,
        totalDeposits,
        totalWithdrawals,
        totalPerformance,
        currentBalance,
        depositCount: approvedDeposits.length,
        withdrawalCount: paidWithdrawals.length,
        monthsActive: userLedgers.length,
        accountOpenDate: firstLedgerDate
      });
    });

    const totalBalance = clients.reduce((s, c) => s + c.currentBalance, 0);
    const totalDepositsSum = clients.reduce((s, c) => s + c.totalDeposits, 0);
    const totalWithdrawalsSum = clients.reduce((s, c) => s + c.totalWithdrawals, 0);

    return {
      success: true,
      data: {
        allClients: clients,
        totalClients: clients.length,
        totalBalance,
        totalDeposits: totalDepositsSum,
        totalWithdrawals: totalWithdrawalsSum,
        totalClientOnlyFund: totalDepositsSum - totalWithdrawalsSum
      }
    };
  } catch (error) {
    console.error('Error fetching client analytics live:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get client analytics from database_calculated_data (client_ledgers_*).
 * Uses pre-calculated data written by script. Prefer getClientAnalyticsLive() so data updates without running script.
 */
export const getClientAnalyticsFromLedgers = async () => {
  try {
    const snapshot = await getDocs(collection(DATABASE, 'database_calculated_data'));
    const clients = [];
    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (data.source !== 'ledgers' || data.type !== 'client') return;
      const currentBalance = parseFloat(data.currentBalance) || 0;
      const totalDeposits = parseFloat(data.totalDeposits) || 0;
      const totalWithdrawals = parseFloat(data.totalWithdrawals) || 0;
      const totalPerformance = parseFloat(data.totalPerformance) || 0;
      const firstLedgerDate = data.firstLedgerDate?.toDate ? data.firstLedgerDate.toDate() : (data.firstLedgerDate ? new Date(data.firstLedgerDate) : null);
      clients.push({
        name: data.name || '—',
        clientNumber: data.userId || null,
        totalDeposits,
        totalWithdrawals,
        totalPerformance,
        currentBalance,
        depositCount: data.depositCount != null ? data.depositCount : 0,
        withdrawalCount: data.withdrawalCount != null ? data.withdrawalCount : 0,
        monthsActive: data.ledgerCount != null ? data.ledgerCount : 0,
        accountOpenDate: firstLedgerDate
      });
    });
    const totalBalance = clients.reduce((sum, c) => sum + c.currentBalance, 0);
    const totalDepositsSum = clients.reduce((sum, c) => sum + c.totalDeposits, 0);
    const totalWithdrawalsSum = clients.reduce((sum, c) => sum + c.totalWithdrawals, 0);
    return {
      success: true,
      data: {
        allClients: clients,
        totalClients: clients.length,
        totalBalance,
        totalDeposits: totalDepositsSum,
        totalWithdrawals: totalWithdrawalsSum,
        totalClientOnlyFund: totalDepositsSum - totalWithdrawalsSum
      }
    };
  } catch (error) {
    console.error('Error fetching client analytics from ledgers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get client analytics data (from fund_history – legacy)
 * Aggregates client performance across all months
 */
export const getClientAnalytics = async () => {
  try {
    const querySnapshot = await getDocs(collection(DATABASE, FUND_HISTORY_COLLECTION));
    
    const clientMap = new Map();
    
    // Aggregate data for each client across all months
    querySnapshot.docs.forEach(doc => {
      const monthData = doc.data();
      
      monthData.clients.forEach(client => {
        if (!clientMap.has(client.name)) {
          clientMap.set(client.name, {
            name: client.name,
            clientNumber: client.clientNumber,
            totalDeposits: 0,
            totalWithdrawals: 0,
            totalPerformance: 0,
            currentBalance: 0,
            monthsActive: 0,
            lifetimeReturn: 0
          });
        }
        
        const clientData = clientMap.get(client.name);
        clientData.totalDeposits += client.deposits;
        clientData.totalWithdrawals += client.withdrawals;
        clientData.totalPerformance += client.netPerformance;
        clientData.currentBalance = client.closingBalance; // Use latest
        clientData.monthsActive += 1;
      });
    });
    
    // Convert map to sorted array
    const clients = Array.from(clientMap.values())
      .sort((a, b) => b.currentBalance - a.currentBalance);
    
    // Calculate top performers
    const topByBalance = clients.slice(0, 10);
    const topByPerformance = [...clients]
      .sort((a, b) => b.totalPerformance - a.totalPerformance)
      .slice(0, 10);
    
    return {
      success: true,
      data: {
        allClients: clients,
        topByBalance,
        topByPerformance,
        totalClients: clients.length,
        totalAUM: clients.reduce((sum, c) => sum + c.currentBalance, 0)
      }
    };
  } catch (error) {
    console.error('Error fetching client analytics:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Compare spreadsheet data with Rowy database
 * Checks for discrepancies in monthly closings
 */
export const compareWithDatabase = async () => {
  try {
    // Get fund history (from spreadsheet)
    const fundHistorySnapshot = await getDocs(collection(DATABASE, FUND_HISTORY_COLLECTION));
    const fundHistory = fundHistorySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Get monthly closings (from database)
    const closingsSnapshot = await getDocs(collection(DATABASE, 'monthly_closings'));
    const closings = closingsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Group closings by user and month
    const closingsByUser = new Map();
    closings.forEach(closing => {
      const key = `${closing.user_id}-${closing.closing_year}-${closing.closing_month}`;
      if (!closingsByUser.has(key)) {
        closingsByUser.set(key, []);
      }
      closingsByUser.get(key).push(closing);
    });
    
    const discrepancies = [];
    
    // Compare each month
    for (const month of fundHistory) {
      for (const client of month.clients) {
        // Find corresponding closing in database
        // Note: This requires user_id which we don't have in spreadsheet
        // We'll compare aggregates instead
        
        // For now, just return structure for future implementation
        // This would require matching client names to user_ids first
      }
    }
    
    return {
      success: true,
      data: {
        totalMonthsChecked: fundHistory.length,
        discrepanciesFound: discrepancies.length,
        discrepancies,
        status: discrepancies.length === 0 ? 'all_match' : 'discrepancies_found'
      }
    };
  } catch (error) {
    console.error('Error comparing with database:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get specific month data
 * @param {number} year 
 * @param {number} month - 0-indexed (0 = January)
 */
export const getMonthData = async (year, month) => {
  try {
    const docId = `${year}-${String(month + 1).padStart(2, '0')}`;
    const docRef = doc(DATABASE, FUND_HISTORY_COLLECTION, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        success: true,
        data: docSnap.data()
      };
    } else {
      return {
        success: false,
        error: `Data not found for ${year}-${month + 1}`
      };
    }
  } catch (error) {
    console.error('Error fetching month data:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Trigger re-import of spreadsheet data
 * This would execute the import script via backend function
 * For now, just returns instruction
 */
export const reimportSpreadsheet = async () => {
  // In production, this would call a Cloud Function or backend API
  // For now, return instructions
  return {
    success: false,
    message: 'Please run the import script manually: node scripts/migration/import-fund-history-from-spreadsheet.js'
  };
};

